/**
 * CrossTypeSpillover — collapsible "see also" panel showing the
 * preference fields from OTHER report types that overlap with the
 * current type (Configuration Upgrade CU5).
 *
 * The static map at src/types/crossTypePrefSpillover.ts says e.g. "when
 * editing feasibility, env's expectedNepaPathway + federalNexus are
 * commonly relevant". This component renders those as inline cards
 * filtered to the report types the site actually uses — so a site
 * that only has feasibility doesn't see hints for env fields it
 * never edits.
 *
 * Setting a value on a cross-type card writes it back via
 * `onSpilloverSet(reportType, fieldKey, value)`. Persistence is the
 * parent's job — typically a write to
 * `report.projectIntent.crossTypeSpillover` so the next env rerun
 * can pre-fill from it.
 *
 * MVP shape: read-only "see also" hint chips. The "set and propagate
 * across types" path is wired but optional — when `onSpilloverSet` is
 * undefined the chips render as informational only.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { ReportTypeId } from '../../../config/reportTypes';
import { spilloverHintsFor } from '../../../types/crossTypePrefSpillover';
import { PREFS_SCHEMA_LABEL } from '../../../types/rerunPrefsByType';

export interface CrossTypeSpilloverProps {
  /** The current report type the user is editing. */
  currentType: ReportTypeId;
  /** Other report types this site has active. Filters the spillover hints. */
  availableOtherTypes: ReportTypeId[];
  /** Optional persistence callback for setting a spillover value. */
  onSpilloverSet?: (
    sourceType: ReportTypeId,
    fieldKey: string,
    value: unknown,
  ) => void;
}

export const CrossTypeSpillover: React.FC<CrossTypeSpilloverProps> = ({
  currentType,
  availableOtherTypes,
  onSpilloverSet: _onSpilloverSet,
}) => {
  const hints = spilloverHintsFor(currentType).filter((h) =>
    availableOtherTypes.includes(h.sourceType),
  );

  if (hints.length === 0) return null;

  // Group by source report type so the user sees env / water /
  // feasibility as separate sub-sections rather than a flat list.
  const byType = hints.reduce<Record<string, typeof hints>>((acc, h) => {
    (acc[h.sourceType] ||= []).push(h);
    return acc;
  }, {});

  return (
    <details
      className="border border-border-subtle p-2 group"
      data-testid="cross-type-spillover"
    >
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center gap-2">
        <ChevronRight
          className="w-3 h-3 text-text-muted transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        <div className="flex-1">
          <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
            Other report types use these fields too
          </div>
          <div className="type-meta text-text-muted mt-0.5">
            Setting them here propagates to the next rerun of the related type.
          </div>
        </div>
      </summary>

      <div className="mt-2 space-y-2">
        {Object.entries(byType).map(([sourceType, group]) => (
          <div
            key={sourceType}
            className="border border-border-subtle p-2 space-y-1"
            data-testid={`cross-type-spillover-${sourceType}`}
          >
            <div className="type-meta uppercase tracking-widest text-text-muted">
              From {PREFS_SCHEMA_LABEL[sourceType as ReportTypeId]}
            </div>
            <ul className="space-y-1">
              {group.map((h) => (
                <li
                  key={`${h.sourceType}-${h.fieldKey}`}
                  className="type-body text-text-secondary"
                  data-testid={`cross-type-hint-${h.sourceType}-${h.fieldKey}`}
                >
                  <span className="font-medium text-text-primary">{h.label}</span>
                  <span className="text-text-muted"> — {h.rationale}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </details>
  );
};
